import { toast } from 'sonner';
import { authFetch } from '@/lib/auth-fetch';
import { getApiUrl } from '@/lib/api-config';
import { dispatchAccessRevoked } from '@/services/accessRevoked';

export const getApiBaseUrl = (): string => {
  let baseUrl: string;
  const configuredUrl = getApiUrl();
  if (configuredUrl) {
    baseUrl = configuredUrl;
  } else {
    baseUrl = import.meta.env.VITE_LIVE_API_BASE_URL ?? 'http://localhost:5000/api';
  }

  const stripped = baseUrl.replace(/\/+$/, '');
  if (!stripped.endsWith('/api')) {
    return `${stripped}/api`;
  }
  return stripped;
};

export class ApiError extends Error {
  public status: number;
  public statusText: string;
  public data?: Record<string, unknown>;

  constructor(status: number, statusText: string, message: string, data?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.data = data;
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorMessage = `API Error: ${response.status} ${response.statusText}`;
    
    let errorData: Record<string, unknown> | undefined;
    try {
      errorData = await response.json();
      errorMessage = (errorData?.message as string) || (errorData?.title as string) || errorMessage;
    } catch {
    }

    if (response.status === 403 && errorData?.error === 'AccessRevoked') {
      dispatchAccessRevoked();
    }
    
    throw new ApiError(response.status, response.statusText, errorMessage, errorData);
  }
  
  if (response.status === 204) {
    return {} as T;
  }
  
  try {
    return await response.json();
  } catch (error) {
    throw new Error('Failed to parse response JSON');
  }
}

export const apiClient = {
  async get<T>(endpoint: string, options?: { signal?: AbortSignal }): Promise<T> {
    const response = await authFetch(`${getApiBaseUrl()}${endpoint}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }, options?.signal);
    return await handleResponse<T>(response);
  },

  async post<T, D = unknown>(endpoint: string, data: D, options?: { signal?: AbortSignal }): Promise<T> {
    const response = await authFetch(`${getApiBaseUrl()}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }, options?.signal);
    return await handleResponse<T>(response);
  },

  async put<T, D = unknown>(endpoint: string, data: D, options?: { signal?: AbortSignal }): Promise<T> {
    const response = await authFetch(`${getApiBaseUrl()}${endpoint}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }, options?.signal);
    return await handleResponse<T>(response);
  },

  async patch<T, D = unknown>(endpoint: string, data?: D, options?: { signal?: AbortSignal }): Promise<T> {
    const response = await authFetch(`${getApiBaseUrl()}${endpoint}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: data !== undefined ? JSON.stringify(data) : undefined,
    }, options?.signal);
    return await handleResponse<T>(response);
  },

  async delete<T>(endpoint: string, options?: { signal?: AbortSignal }): Promise<T> {
    const response = await authFetch(`${getApiBaseUrl()}${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    }, options?.signal);
    return await handleResponse<T>(response);
  },
};
